import React, { useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { Button } from 'components/Elements';
import LogoImg from 'assets/logo.png';
import { FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const COLORS = ['#231F20', '#FFFFFF', '#8884d8', '#82ca9d', '#ffc658'];

const tokenAllocations = [
    { name: 'Community Grants', value: 40 },
    { name: 'DAO Treasury', value: 25 },
    { name: 'Team & Contributors', value: 15 },
    { name: 'Adoption & Growth', value: 10 },
    { name: 'Reserve', value: 10 },
];

export const WelcomeToAshita = () => {
    const creativeRef = useRef(null);
    const donorRef = useRef(null);
    const holderRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="p-6 space-y-12 max-w-6xl mx-auto text-sm">
            <Link to="/">
                <motion.img
                    src={LogoImg}
                    alt="Ashita Creatives DAO"
                    className="w-32 mx-auto"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                /></Link>

            <div className="text-center space-y-4">
                <h1 className="text-3xl font-bold">Welcome to Ashita Creatives DAO</h1>
                <p>
                    Ashita Creatives DAO is a community-owned space where <strong>creatives</strong>, <strong>donors</strong>, and <strong>supporters</strong> collaborate to fund, create, and own the future of African creativity.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <Button onClick={() => scrollToSection(creativeRef)}>I'm a Creative</Button>
                    <Button onClick={() => scrollToSection(donorRef)}>I'm a Donor</Button>
                    <Button onClick={() => scrollToSection(holderRef)}>I'm a Token Holder</Button>
                </div>
            </div>

            <section ref={creativeRef}>
                <h2 className="text-2xl font-semibold mb-2">For Creatives</h2>
                <p>
                    As a creative, Ashita is your playground. Whether you're a designer, musician, writer, videographer, or illustrator, you can:
                </p>
                <ul className="list-disc pl-6">
                    <li>Apply for <strong>community grants</strong> to fund your work</li>
                    <li>Collaborate on DAO-backed campaigns and products</li>
                    <li>Build your portfolio and earn reputation</li>
                    <li>Get exposure via DAO social media and NFT marketplaces</li>
                </ul>
                <p className="mt-2">
                    <strong>Scenario;</strong> A visual artist applies for 300 USDC to create an NFT collection showcasing East African traditions. The DAO reviews and funds it. Proceeds are split transparently between the artist and the DAO.
                </p>
            </section>

            <section ref={donorRef}>
                <h2 className="text-2xl font-semibold mb-2">For Donors</h2>
                <p>
                    As a donor, you can help launch real impact projects while enjoying transparency and community involvement. Your benefits include:
                </p>
                <ul className="list-disc pl-6">
                    <li>Support verified creatives and causes</li>
                    <li>Access updates and voting rights (via token/NFT)</li>
                    <li>Receive honorary NFTs and shoutouts</li>
                    <li>Join curated donor-only meetups and reports</li>
                </ul>
                <p className="mt-2">
                    <strong>Scenario;</strong> You donate $100 to fund a storytelling docuseries about local heroes. You're sent behind-the-scenes access, governance tokens, and an honorary thank-you NFT.
                </p>
            </section>

            <section ref={holderRef}>
                <h2 className="text-2xl font-semibold mb-2">For Token Holders</h2>
                <p>
                    Holding the Ashita DAO token gives you governance rights and shared ownership of what we build. Here's what you can do:
                </p>
                <ul className="list-disc pl-6">
                    <li>Vote on proposals, grants, and campaigns</li>
                    <li>Stake tokens for voting rewards or benefits</li>
                    <li>Participate in treasury allocation decisions</li>
                    <li>Co-create DAO policies and project pipelines</li>
                </ul>
                <p className="mt-2">
                    <strong>Scenario;</strong> As a holder, you vote to allocate 20% of the monthly treasury toward a music festival organized by DAO members. You help shape what Ashita funds.
                </p>
            </section>

            <section>
                <h2 className="text-2xl font-semibold mb-4">Token Allocation</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={tokenAllocations}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                        >
                            {tokenAllocations.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </section>

            <section className="text-center space-y-2">
                <h2 className="text-2xl font-semibold">Join Our Community</h2>
                <p>Tap in. Collaborate. Co-create. Vote. Launch projects.</p>
                <div className="flex justify-center gap-6 text-xl">
                    <a href="https://t.me/ashitacreativesDAO" target="_blank" rel="noopener noreferrer">
                        <FaTelegram className="hover:text-blue-400" />
                    </a>
                    <a href="https://chat.whatsapp.com/KLyYODzz9xy10AVK1bgyn7" target="_blank" rel="noopener noreferrer">
                        <FaWhatsapp className="hover:text-green-500" />
                    </a>
                </div>
            </section>
        </div>
    );
};
